import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  IGenericServiceResponse,
  IServiceResponse,
} from '../interfaces/IServiceResponse';
import { environment } from 'src/environments/environment';
import { EpisodeOfCareService } from './episodeOfCare.service';
import { PatientService } from './patient.service';
import { ICareTeam } from '../interfaces/CareTeam/ICareTeam';
import { IAddCareTeam } from '../interfaces/CareTeam/IAddCareTeam';
import { IUpdateCareTeam } from '../interfaces/CareTeam/IUpdateCareTeam';
import { SwalToastService } from './swal.service';

const CARE_TEAM_STATUS_BADGE_CLASS: { [code: string]: string } = {
  active: 'badge-approved',
  proposed: 'badge-pending',
  suspended: 'badge-orange',
  inactive: 'badge-inactive',
  'entered-in-error': 'badge-red',
};

@Injectable({ providedIn: 'root' })
export class CareTeamService {
  public careTeamModalSub = new BehaviorSubject<boolean>(false);
  public careTeamEditSub = new BehaviorSubject<ICareTeam | undefined>(
    undefined,
  );
  public careTeamReadOnlySub = new BehaviorSubject<boolean>(false);

  public careTeamActiveDataSub = new BehaviorSubject<ICareTeam[]>([]);
  public careTeamPastDataSub = new BehaviorSubject<ICareTeam[]>([]);

  public careTeamLoadingSub = new BehaviorSubject<boolean>(false);
  public pastCareTeamLoadingSub = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly _http: HttpClient,
    private readonly eocService: EpisodeOfCareService,
    private readonly patientService: PatientService,
    private readonly swalToastService: SwalToastService,
  ) {}

  async getData() {
    this.careTeamLoadingSub.next(true);
    this.pastCareTeamLoadingSub.next(true);
    this.getCareTeam(
      this.patientService.patientIDsignal(),
      this.eocService.currentVisitId(),
    ).subscribe({
      next: (response) => {
        const careTeams = (response?.data || []).map((careTeam) => ({
          ...careTeam,
          onSetDate: careTeam.onSetDateTime,
          endDate: careTeam.endDateTime,
          __badge__careTeamStatusDisplay: {
            text: careTeam.careTeamStatusDisplay,
            className:
              CARE_TEAM_STATUS_BADGE_CLASS[careTeam.careTeamStatusCode] ?? '',
          },
        }));
        this.careTeamActiveDataSub.next(
          careTeams.filter((careTeam) => careTeam.isActive),
        );
        this.careTeamPastDataSub.next(
          careTeams
            .filter((careTeam) => !careTeam.isActive)
            .map((careTeam) => ({ ...careTeam, isStopped: true })),
        );
        this.careTeamLoadingSub.next(false);
        this.pastCareTeamLoadingSub.next(false);
      },
      error: (err) => {
        this.swalToastService.toastError('Unable to retrieve care teams');
        console.error('Error fetching care teams:', err);
        this.careTeamLoadingSub.next(false);
        this.pastCareTeamLoadingSub.next(false);
      },
    });
  }

  getCareTeam(
    patientId: string,
    visitId?: string,
  ): Observable<IGenericServiceResponse<ICareTeam[]>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    if (visitId) {
      return this._http.get<IGenericServiceResponse<ICareTeam[]>>(
        `${environment.BACKEND_URL}CareTeam/${patientId}?visitId=${visitId}`,
        { headers },
      );
    }
    return this._http.get<IGenericServiceResponse<ICareTeam[]>>(
      `${environment.BACKEND_URL}CareTeam/${patientId}`,
      { headers },
    );
  }

  getActiveCareTeams(
    patientId: string,
    visitId?: string
  ): Observable<IGenericServiceResponse<ICareTeam[]>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    if (visitId) {
      return this._http.get<IGenericServiceResponse<ICareTeam[]>>(
        `${environment.BACKEND_URL}CareTeam/ActiveCareTeams/${patientId}?visitId=${visitId}`,
        { headers }
      );
    }
    return this._http.get<IGenericServiceResponse<ICareTeam[]>>(
      `${environment.BACKEND_URL}CareTeam/ActiveCareTeams/${patientId}`,
      { headers }
    );
  }

  assignCareTeam(
    careTeamTemplateId: number,
    onSetDateTime?: string,
    endDateTime?: string,
  ): Observable<IGenericServiceResponse<ICareTeam>> {
    const careTeamObj: IAddCareTeam = {
      careTeamTemplateId: careTeamTemplateId,
      patientId: this.patientService.patientIDsignal(),
      visitId: this.eocService.currentVisitId(),
      episodeCareId: this.eocService.currentEocId(),
      onSetDateTime: onSetDateTime,
      endDateTime: endDateTime,
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.post<IGenericServiceResponse<ICareTeam>>(
      `${environment.BACKEND_URL}CareTeam`,
      careTeamObj,
      {
        headers: headers,
      },
    );
  }

  updateCareTeam(
    careTeamId: string,
    careTeamTemplateId: number,
    onSetDateTime?: string,
    endDateTime?: string,
  ): Observable<IServiceResponse> {
    const careTeamObj: IUpdateCareTeam = {
      careTeamTemplateId: careTeamTemplateId,
      onSetDateTime: onSetDateTime,
      endDateTime: endDateTime,
      visitId: this.eocService.currentVisitId(),
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.put<IServiceResponse>(
      `${environment.BACKEND_URL}CareTeam/${careTeamId}`,
      careTeamObj,
      {
        headers: headers,
      },
    );
  }

  deleteCareTeam(careTeamId: string): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.delete<IServiceResponse>(
      `${environment.BACKEND_URL}CareTeam/${careTeamId}`,
      {
        headers: headers,
      },
    );
  }

  unassignCareTeam(careTeamId: string): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const visitId = this.eocService.currentVisitId();

    return this._http.put<IServiceResponse>(
      `${environment.BACKEND_URL}CareTeam/Unassign/${careTeamId}/${visitId}`,
      {},
      {
        headers: headers,
      },
    );
  }

  revertUnassignCareTeam(careTeamId: string): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const visitId = this.eocService.currentVisitId();

    return this._http.put<IServiceResponse>(
      `${environment.BACKEND_URL}CareTeam/RevertUnassign/${careTeamId}/${visitId}`,
      {},
      {
        headers: headers,
      },
    );
  }
}
